import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChild,
  DestroyRef,
  ElementRef,
  HostListener,
  inject,
  Input,
  OnChanges,
  OnInit,
  ViewChild,
  AfterViewInit
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ButtonOptions,
  ButtonToggleComponent,
  LoaderModule,
  ObjectCardComponent,
  SearchBarComponent,
  SearchFilterComponent,
  SearchPlaceholderComponent,
  TabComponent
} from '@mds/ui';
import { ModifiedSearchPayload, SEARCH_SCHEMAS_V2, SEARCH_VIEW_TABS, SearchPayload, SearchType } from '@mds/common';
import { MdsIcon, MdsIconsModule } from '@mds/icons';
import { HeaderService } from './services/header-search-api.service';
import { HeaderReusableService } from './services/header-search-reusable.service';
import { MatDialog } from '@angular/material/dialog';
import { GlobalDataService } from '@services/global-data.service';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { MatMenuModule } from '@angular/material/menu';
import { catchError, debounceTime, filter, map, of, Subject, switchMap, takeUntil, tap } from 'rxjs';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MenuService } from '@services/menu.service';
import { RouterHelperService } from '@services/router-helper.service';
import { MatButtonToggleChange } from '@angular/material/button-toggle';
import { MaterialCatalog } from '@components/material-catalog/material-catalog';

@Component({
  selector: 'app-header-search',
  standalone: true,
  imports: [
    CommonModule,
    SearchBarComponent,
    ObjectCardComponent,
    TabComponent,
    MdsIconsModule,
    SearchPlaceholderComponent,
    MatMenuModule,
    LoaderModule,
    SearchFilterComponent,
    ReactiveFormsModule,
    ButtonToggleComponent,
    MaterialCatalog
  ],
  providers: [HeaderService, HeaderReusableService],
  templateUrl: './header-search.component.html',
  styleUrls: ['./header-search.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class HeaderSearchComponent implements OnInit, OnChanges, AfterViewInit {
  productList = [];
  page = 1;
  nextPageToken = null;
  loading = false;
  appName = {
    name: 'app.dashboard'
  };
  payload: SearchPayload;
  currentFilters: any;

  isLargeScreen: boolean = window.innerWidth > 1000;
  tabHasScroll = false;
  searchSchema = SEARCH_SCHEMAS_V2['app.dashboard'];
  icons = MdsIcon;
  inputText: string;
  currentTab: any;
  firstSearchFlag = true;
  showResultSection = true;
  @ContentChild('tabContent') content: any; // Querying content using ContentChild
  @ViewChild('tabContainer') tabContainer: ElementRef;
  showContent = false;
  currentApp: any;
  lookupApiUrl: string;
  callNewPage = true;
  scrollingStarted = false;
  recentSearchList = [];
  searchControl: FormControl = new FormControl<string>('');
  private headerService = inject(HeaderService);
  private routerHelperService = inject(RouterHelperService);
  private headerReusableService = inject(HeaderReusableService);
  private menuService = inject(MenuService);
  private cancelPreviousRequest = new Subject<void>();
  private destroyRef = inject(DestroyRef);
  private route = inject(ActivatedRoute);
  private searchTrigger$ = new Subject<{
    keyword: string;
    page: number;
    next_token?: string | null;
    isScroll: boolean;
    filters?: any;
  }>();
  @Input() focusOff: boolean;

  selectedTab: SearchType = SearchType.GLOBAL;
  tabs: ButtonOptions[] = SEARCH_VIEW_TABS;
  SearchTypeEnum = SearchType;
  constructor(
    private cdr: ChangeDetectorRef,
    public dialog: MatDialog,
    public globalDataService: GlobalDataService,
    private router: Router
  ) {}

  onSwitchTabView(tab: MatButtonToggleChange) {
    this.selectedTab = tab.value;
    if (this.selectedTab === SearchType.MATERIAL_CATALOG) {
      this.searchControl.disable();
    } else {
      this.searchControl.enable();
      this.inputText = this.searchControl.value || '';
    }
  }

  ngOnChanges() {
    if (this.focusOff) this.closeMenu();
  }

  @HostListener('window:resize', ['$event'])
  onResize() {
    this.isLargeScreen = window.innerWidth > 960;
    this.hasMatTabPagination();
  }

  ngAfterViewInit() {
    this.onResize();
  }

  hasMatTabPagination() {
    setTimeout(() => {
      this.tabHasScroll = !!this.tabContainer.nativeElement.querySelector(
        '.mat-mdc-tab-header-pagination-controls-enabled'
      );
    }, 0);
  }

  ngOnInit(): void {
    this.loading = false;
    this.isLargeScreen = window.innerWidth > 960;
    if (localStorage.pastSearches) {
      const recentSearchData = JSON.parse(localStorage.pastSearches);
      this.recentSearchList = recentSearchData;
    } else {
      this.recentSearchList = [];
    }
    this.startSearchListener();
    this.handleSearchResult();
    this.initializeSearchDataOnDashboardNavigation();
  }

  initializeSearchDataOnDashboardNavigation() {
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        let currentRoute = this.route.root;
        while (currentRoute.firstChild) {
          currentRoute = currentRoute.firstChild;
        }
        // Check the data in the leaf route (the actual active component)
        if (currentRoute.snapshot.data['clearSearch']) {
          this.searchControl.setValue('', { emitEvent: false });
        }
      });
  }

  getActiveTab(tab) {
    this.currentTab = tab;
    this.resetSearch();
    // trigger unified search pipeline if inputText exists
    if (this.inputText?.length > 0) {
      this.searchTrigger$.next({
        keyword: this.inputText,
        page: 1,
        isScroll: false
      });
    }
  }

  receiveText(text: string) {
    this.inputText = text;
    this.page = 1;
    this.productList = [];
    this.fetchResults();
  }

  handleSearchResult() {
    this.searchTrigger$
      .pipe(
        tap(() => (this.loading = true)),
        switchMap(({ keyword, page, next_token, isScroll, filters }) => {
          this.inputText = keyword;
          this.page = page;

          const tabValue = this.currentTab.value.toLowerCase();
          const apiUrl = `${this.searchSchema.endpoint}${this.page}&tab=${tabValue}`;
          this.lookupApiUrl = apiUrl;
          this.firstSearchFlag = false;

          // Build payload using keyword + filters
          this.payload = {
            keyword,
            ...(filters || {})
          };

          if (this.currentTab.formSchema.length === 0) {
            this.payload = { keyword };
          }

          if (next_token) {
            this.payload.next_token = next_token;
          }

          this.cancelPreviousRequest.next();

          return this.headerService.fetchSearchResults(apiUrl, this.payload).pipe(
            takeUntil(this.cancelPreviousRequest),
            map((res) => ({ res, isScroll })),
            catchError((error) => {
              // Optional: log or handle error centrally

              // Emit a fallback object to continue the stream
              return of({ res: null, isScroll, error: true });
            })
          );
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: ({ res, isScroll }) => this.handleSearchResponse(res, isScroll),
        error: () => {
          this.loading = false;
          this.scrollingStarted = false;
        }
      });
  }

  resetSearch() {
    this.productList = [];
    this.page = 1;
    this.nextPageToken = null;
    this.callNewPage = false;
  }

  startSearchListener() {
    this.searchControl.valueChanges
      .pipe(
        debounceTime(300),
        tap(() => this.resetSearch()),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((value) => {
        this.cdr.detectChanges();
        this.searchTrigger$.next({
          keyword: value,
          page: 1,
          isScroll: false,
          filters: this.currentFilters
        });
      });
  }

  toggleSections() {
    this.showResultSection = !this.showResultSection;
  }

  updateFilter(type) {
    this.currentTab = type;
  }

  fetchResults() {
    this.loading = true;

    const tabValue = this.currentTab.value.toLowerCase();
    const apiUrl = `${this.searchSchema.endpoint}${this.page}&tab=${tabValue}`;
    this.lookupApiUrl = apiUrl;
    this.firstSearchFlag = false;
    this.payload.keyword = this.inputText;

    if (this.currentTab.formSchema.length === 0) {
      this.payload = { keyword: this.payload.keyword };
    }
    if (this.nextPageToken) {
      this.payload.next_token = this.nextPageToken;
    }

    this.cancelPreviousRequest.next();

    this.headerService
      .fetchSearchResults(apiUrl, this.payload)
      .pipe(takeUntil(this.cancelPreviousRequest))
      .subscribe({
        next: (res) => this.handleSearchResponse(res, true),
        error: () => {
          this.loading = false;
          this.scrollingStarted = false;
          this.callNewPage = true;
        }
      });
  }

  /** remove label and keep value in array of source filter */
  removeLabel(payload: SearchPayload, value): ModifiedSearchPayload {
    return {
      status: payload?.status,
      availability: payload?.availability,
      source: payload?.source?.map((item) => item.value),
      geography_codes: payload?.geo?.map((geo) => geo.value),
      keyword: value
    };
  }

  getPlaceHolder() {
    if (this.loading && this.inputText?.length > 0) {
      return 'loading';
    }
    if (!this.inputText?.length && !this.recentSearchList.length) {
      return 'start';
    }
    if (!this.productList?.length && !this.loading && this.inputText?.length > 0) {
      return 'empty';
    }
    return '';
  }

  closeMenu() {
    this.showContent = false;
    this.inputText = '';
    this.disableScroll(false);
    this.resetTab();
  }

  isDropdown(event: boolean) {
    this.showContent = event;
    this.disableScroll(true);
    this.resetTab();
  }

  openMenuItem(data: any) {
    this.routerHelperService.openMenuItem(data).subscribe(() => {
      this.showContent = false;
      this.resetTab();
      this.cdr.markForCheck();
    });
  }

  onEnter(data) {
    const currentApp = {
      name: 'app.dashboard'
    };
    const lookupData = {
      searchText: data,
      data: currentApp,
      lookUpApiUrl: 'globalsearch?page=1'
    };
    // TO-DO scope-3 dashboard new design does not send data to global data service hence used localstorage
    localStorage.setItem('scope3_lookup', JSON.stringify(lookupData));
    this.router.navigate(['/lookup-tool/']);
    this.globalDataService.sendSearchText({
      searchText: data,
      data: currentApp,
      lookUpApiUrl: 'globalsearch?page=1'
    });
    this.closeMenu();
  }

  onScroll(ev) {
    const tracker = ev.target;
    const limit = (tracker.scrollHeight - tracker.clientHeight) * 0.7;
    if (
      ev.target.scrollTop >= limit &&
      this.callNewPage &&
      ((!this.nextPageToken && this.page === 1) || this.nextPageToken)
    ) {
      this.callNewPage = false;
      this.page++;
      this.scrollingStarted = true;

      this.searchTrigger$.next({
        keyword: this.inputText,
        page: this.page,
        next_token: this.nextPageToken,
        isScroll: true,
        filters: this.currentFilters
      });
    }
  }

  openProduct(selectedItem: any) {
    const activeApp = this.menuService.getActiveApp();
    const activeAppLayer = activeApp?.layer ?? '';
    const rid = selectedItem.rid.replace('#', '');
    let segments: string[] = [];
    if (activeAppLayer) {
      if (activeAppLayer === 'explorer') {
        segments = ['/', activeAppLayer, rid];
      } else {
        segments = ['/', 'mattermaps', activeAppLayer, rid];
      }
    } else {
      segments = ['/', 'mattermaps', 'overview', rid];
    }
    const currentUrl = this.router.url.split('?')[0].split('#')[0];
    if (currentUrl.endsWith('/edit')) {
      segments.push('edit');
    }
    if (segments.length) {
      this.showContent = false;
      this.resetTab();
      this.router.navigate(segments);
    }
    if (this.recentSearchList.find((searchItem) => searchItem.rid === selectedItem.rid) === undefined) {
      this.recentSearchList.unshift(selectedItem);
      if (this.recentSearchList.length > 10) this.recentSearchList.pop();
      localStorage.pastSearches = JSON.stringify(this.recentSearchList);
    }
  }

  normalizeFilters(filters: any) {
    if (!filters || typeof filters !== 'object') {
      return {}; // return empty object if filters is null/undefined/not an object
    }

    const result: Record<string, any> = {};

    Object.entries(filters).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        // If all array items are objects with `.value`, convert them
        if (value.every((v) => typeof v === 'object' && v !== null && 'value' in v)) {
          result[key] = value.map((v) => v.value);
        } else {
          result[key] = value;
        }
      } else {
        result[key] = value;
      }
    });

    return result;
  }

  filterValueChanged(event) {
    this.resetSearch(); // resets page, productList, nextPageToken, etc.

    let filterPayload = this.filterPayload(this.currentTab.formSchema, event.payload);

    filterPayload = this.normalizeFilters(filterPayload);

    // Optional: Build param list for debugging/logging, not required for API
    const paramList: string[] = [];

    if (filterPayload) {
      Object.keys(filterPayload).forEach((key) => {
        const value = filterPayload[key];
        if (!value) return;

        if (Array.isArray(value)) {
          value.forEach((v) => paramList.push(`${key}=${encodeURIComponent(v?.value || v)}`));
        } else {
          paramList.push(`${key}=${encodeURIComponent(value)}`);
        }
      });
    }

    this.currentFilters = this.cleanFilters(filterPayload);
    // Trigger unified search only if there's a keyword and some filters
    if (
      event?.payload &&
      (Object.keys(event.payload).length > 0 || event.payload !== '{}') &&
      this.inputText?.length > 1
    ) {
      this.searchTrigger$.next({
        keyword: this.inputText,
        page: 1,
        isScroll: false,
        filters: this.currentFilters
      });
    }
  }

  filterPayload(formSchema, payload: SearchPayload) {
    if (!payload) {
      return;
    }
    const result = Object.entries(payload).reduce((acc, [key, value]) => {
      const foundItem = formSchema.find((item) => item.key === key);

      if (foundItem) {
        if (value !== null) {
          if (foundItem.enum) {
            // Ensure value is an array before calling filter
            if (Array.isArray(value)) {
              // Filter values that match enum in formSchema
              const validValues = value.filter((val: string) => foundItem.enum.some((en: any) => en.value === val));
              acc[key] = validValues.length > 0 ? validValues : null; // Assign null if no valid values found
            } else {
              acc[key] = null; // If value is not an array, assign null
            }
          } else {
            acc[key] = value; // If no enum, assign the value directly
          }
        } else {
          acc[key] = null; // Assign null if payload value is null
        }
      } else {
        acc[key] = null; // Assign null if key not found in formSchema
      }

      return acc;
    }, {} as any);

    // Add keyword from payload
    if (payload.keyword) {
      result.keyword = payload.keyword;
    }
    return result;
  }

  filtersUpdate(event) {
    /** refresh the filter component when filters are clear from placeholder component */
    this.currentTab = { ...this.currentTab, refresh: 'updated' };
    this.filterValueChanged(event);
  }

  private handleSearchResponse(res: any, isScroll = false): void {
    this.loading = false;
    this.scrollingStarted = false;
    this.nextPageToken = res?.next_token;

    const response = res?.search_results || res?.results || res?.lines || res?.radio || res || [];

    if (response.length) {
      this.callNewPage = true;
    }

    // For scroll append, else replace
    this.productList = isScroll ? [...this.productList, ...response] : response;

    this.cdr.detectChanges();
  }

  private cleanFilters(filters: any): any {
    const cleaned = {};

    Object.entries(filters).forEach(([key, value]) => {
      if (
        value === null ||
        value === undefined ||
        (typeof value === 'string' && value.trim() === '') ||
        (Array.isArray(value) && value.length === 0)
      ) {
        return; // skip
      }

      cleaned[key] = value;
    });

    return cleaned;
  }

  private disableScroll(scrollable: boolean) {
    // TODO temporary function until merge new design
    document
      .querySelector('app-layout')
      ?.shadowRoot?.querySelector('.v2-container')
      ?.classList.toggle('no-scroll', scrollable);
  }

  private resetTab() {
    this.selectedTab = SearchType.GLOBAL;
    this.searchControl.enable();
  }
}
